import { Capacitor } from '@capacitor/core'
import { Filesystem, Directory } from '@capacitor/filesystem'

/**
 * Downloads a file (blob) to the device
 * On Android: saves to Downloads directory using Filesystem plugin
 * On Web: uses standard browser download
 */
export async function downloadFile(
  blob: Blob,
  filename: string,
): Promise<void> {
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
    // Android: Use Filesystem plugin to save to Downloads directory
    try {
      // Convert blob to base64
      const base64Data = await blobToBase64(blob)

      // Try to save to Downloads directory first
      // Note: Directory.ExternalStorage may not work on all Android versions
      // Directory.Documents is more reliable but saves to app-specific folder
      try {
        await Filesystem.writeFile({
          path: `Download/${filename}`, // Try Downloads folder
          data: base64Data,
          directory: Directory.ExternalStorage,
          recursive: true,
        })
      } catch (externalError) {
        // Fallback to Documents directory if ExternalStorage fails
        // This saves to app-specific Documents folder (still accessible via file manager)
        console.warn(
          'Could not save to ExternalStorage, using Documents:',
          externalError,
        )
        await Filesystem.writeFile({
          path: filename,
          data: base64Data,
          directory: Directory.Documents,
          recursive: true,
        })
      }
    } catch (error) {
      console.error('Error saving file on Android:', error)
      throw error
    }
  } else {
    // Web: Use standard browser download
    const downloadLink = document.createElement('a')
    downloadLink.href = URL.createObjectURL(blob)
    downloadLink.download = filename
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
    URL.revokeObjectURL(downloadLink.href)
  }
}

/**
 * Converts a Blob to base64 string
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64String = reader.result as string
      // Remove data URL prefix (e.g., "data:application/zip;base64,")
      const base64Data = base64String.split(',')[1] || base64String
      resolve(base64Data)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}
